/**
 * QuestionQueue.tsx — Decisions
 *
 * The questions the mapping cannot answer on its own. Sorted by severity,
 * answered through structured option cards with sub-inputs. After an answer
 * is applied the list is refetched and the project stats refresh.
 */

import React, { useState, useEffect } from 'react';
import {
  ConversionProject,
  ConversionQuestion,
  QuestionOption,
  getQuestions,
  applyAnswer,
} from '../../services/conversionArchitectAPI';
import {
  c, font, eyebrow, panel, inputStyle, pill,
  btnPrimary, btnSecondary,
  QTYPE_LABELS, SEVERITY_CHIPS,
} from './tokens';
import Toast from './Toast';

/**
 * Render a context value as readable account lines instead of raw JSON.
 * Objects with code/name become "code — name"; arrays of them, one per line.
 */
function renderContextValue(val: unknown): React.ReactNode {
  const asAccountLine = (o: any, key?: React.Key) => (
    <div key={key} style={{ lineHeight: 1.6 }}>
      <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 11.5 }}>
        {String(o.code ?? o.account_code ?? '')}
      </span>
      {(o.name ?? o.account_name) ? <span>{' — '}{String(o.name ?? o.account_name)}</span> : null}
      {o.type ? <span style={{ opacity: 0.7 }}>{' · '}{String(o.type)}</span> : null}
    </div>
  );
  const isAccountish = (o: any) =>
    o && typeof o === 'object' && !Array.isArray(o) &&
    (o.code != null || o.account_code != null);

  if (Array.isArray(val) && val.length > 0 && val.every(isAccountish)) {
    return <div>{val.map((o, i) => asAccountLine(o, i))}</div>;
  }
  if (isAccountish(val)) return asAccountLine(val);
  if (typeof val === 'object' && val !== null) {
    return (
      <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 11 }}>
        {JSON.stringify(val)}
      </span>
    );
  }
  return String(val);
}

interface QuestionQueueProps {
  project: ConversionProject;
  onPhaseComplete: () => void;
  onRefresh: () => void;
}

const SEVERITY_LABELS: Record<string, string> = {
  critical: 'Blocking',
  high: 'Important',
  medium: 'Worth a look',
};

function qTypeLabel(t: string): string {
  return QTYPE_LABELS[t] || 'Needs your decision';
}

function severityChip(severity: string): React.ReactNode {
  const s = SEVERITY_CHIPS[severity] || SEVERITY_CHIPS.medium;
  return <span style={pill(s.bg, s.fg, s.border)}>{SEVERITY_LABELS[severity] || severity}</span>;
}

const QuestionQueue: React.FC<QuestionQueueProps> = ({
  project,
  onPhaseComplete,
  onRefresh,
}) => {
  const [questions, setQuestions] = useState<ConversionQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeQuestion, setActiveQuestion] = useState<ConversionQuestion | null>(null);
  const [selectedOption, setSelectedOption] = useState<string>('');
  const [answerData, setAnswerData] = useState<Record<string, any>>({});
  const [submitting, setSubmitting] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'open' | 'all'>('open');
  const [toast, setToast] = useState('');

  useEffect(() => {
    loadQuestions(true);
  }, [project.id, statusFilter]);

  const loadQuestions = async (selectFirst: boolean) => {
    setLoading(true);
    try {
      const data = await getQuestions(project.id, {
        status: statusFilter === 'open' ? 'open' : undefined,
        sort: 'severity',
      });
      setQuestions(data);
      if (selectFirst) {
        const firstOpen = data.find(q => q.status === 'open') || data[0] || null;
        setActiveQuestion(firstOpen);
      }
    } catch (err: any) {
      console.error('Failed to load questions:', err);
      setToast('Could not load the open decisions');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOption = (option: QuestionOption) => {
    setSelectedOption(option.label);
    // Reset answer data for sub-inputs
    const newData: Record<string, any> = {};
    if (option.sub_inputs) {
      for (const input of option.sub_inputs) {
        newData[input.key] = '';
      }
    }
    setAnswerData(newData);
  };

  const handleSubInputChange = (key: string, value: any) => {
    setAnswerData(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmitAnswer = async () => {
    if (!activeQuestion || !selectedOption) return;
    setSubmitting(true);
    try {
      const chosen = activeQuestion.options.find(o => o.label === selectedOption);
      const result = await applyAnswer(project.id, activeQuestion.id, {
        selected_option: selectedOption,
        answer_data: answerData,
        preview_statement: chosen?.preview_statement,
      });

      setToast(result.preview_statement || 'Answer recorded');
      setSelectedOption('');
      setAnswerData({});
      setActiveQuestion(null);
      // Server contract: applyAnswer returns { answer, question_status,
      // preview_statement } — refetch the queue and project stats.
      await loadQuestions(true);
      onRefresh();
    } catch (err: any) {
      console.error('Failed to apply answer:', err);
      setToast(err.response?.data?.error || 'Failed to apply the answer');
    } finally {
      setSubmitting(false);
    }
  };

  const stats = project.summary_stats;
  // Server sends questions_total and questions_open — answered is the difference.
  // Never derive from the loaded list: the "Still open" filter would zero it.
  const answeredTotal =
    stats?.questions_total != null && stats?.questions_open != null
      ? stats.questions_total - stats.questions_open
      : questions.filter(q => q.status !== 'open').length;
  const questionsTotal = stats?.questions_total ?? questions.length;
  const progressPct = questionsTotal > 0 ? Math.min(100, (answeredTotal / questionsTotal) * 100) : 0;

  if (loading && questions.length === 0) {
    return (
      <div style={{ ...panel, fontFamily: font.ui, fontSize: 13, color: c.textSecondary }}>
        Loading decisions…
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div style={{ fontFamily: font.ui }}>
        <div style={{ ...panel, textAlign: 'center', padding: '48px 24px' }}>
          <h2 style={{ margin: '0 0 10px', fontSize: 20, fontWeight: 800, color: c.green.text }}>
            Every decision is closed
          </h2>
          <p style={{ margin: '0 auto 20px', maxWidth: 480, fontSize: 13, color: c.textBody }}>
            Nothing here is waiting on you. Run the checks next.
          </p>
          <button
            type="button"
            style={{ ...btnPrimary(), fontSize: 14, padding: '11px 22px' }}
            onClick={onPhaseComplete}
          >
            Run the checks →
          </button>
        </div>
        <Toast message={toast} onClose={() => setToast('')} />
      </div>
    );
  }

  return (
    <div style={{ fontFamily: font.ui }}>
      {/* Progress + filter bar */}
      <div style={{ ...panel, padding: '14px 20px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 260px', minWidth: 220 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: c.tableLabel }}>
              {answeredTotal} of {questionsTotal} answered
            </span>
          </div>
          <div style={{ height: 6, borderRadius: 999, background: c.border, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${progressPct}%`, background: c.green.text2, borderRadius: 999 }} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {(['open', 'all'] as const).map(f => {
            const active = statusFilter === f;
            return (
              <button
                key={f}
                type="button"
                onClick={() => setStatusFilter(f)}
                style={{
                  ...(active
                    ? { ...btnSecondary(), background: c.textPrimary, color: '#fff', border: `1px solid ${c.textPrimary}` }
                    : btnSecondary()),
                  fontSize: 12,
                  padding: '6px 14px',
                }}
              >
                {f === 'open' ? 'Still open' : 'All decisions'}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 360px) 1fr', gap: 16, alignItems: 'start' }}>
        {/* Question list */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {questions.map(q => {
            const isActive = activeQuestion?.id === q.id;
            const answered = q.status !== 'open';
            return (
              <button
                key={q.id}
                type="button"
                onClick={() => {
                  setActiveQuestion(q);
                  setSelectedOption('');
                  setAnswerData({});
                }}
                style={{
                  textAlign: 'left',
                  background: c.card,
                  border: answered
                    ? `1px solid ${c.green.border}`
                    : isActive
                      ? `1px solid ${c.textPrimary}`
                      : `1px solid ${c.border}`,
                  borderRadius: 10,
                  padding: '12px 14px',
                  cursor: 'pointer',
                  fontFamily: font.ui,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                  {answered ? (
                    <span style={pill(c.green.text2, '#fff')}>✓ Answered</span>
                  ) : (
                    <span style={pill(c.amber.fill, c.amber.text, c.amber.border)}>Waiting on you</span>
                  )}
                  {!answered && severityChip(q.severity)}
                </div>
                <div style={{ fontSize: 11.5, color: c.textSecondary, marginBottom: 3 }}>
                  {qTypeLabel(q.question_type)}
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: c.textPrimary, lineHeight: 1.4 }}>
                  {q.title}
                </div>
                {answered && (q.answered_by || q.answered_at) && (
                  <div style={{ fontSize: 11, color: c.textTertiary, marginTop: 5 }}>
                    {q.answered_by ? `Answered by ${q.answered_by}` : 'Answered'}
                    {q.answered_at ? ` · ${new Date(q.answered_at).toLocaleString()}` : ''}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Active question detail */}
        {activeQuestion && (
          <div style={panel}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
              {severityChip(activeQuestion.severity)}
              <span style={{ fontSize: 11.5, color: c.textSecondary, fontWeight: 700 }}>
                {qTypeLabel(activeQuestion.question_type)}
              </span>
            </div>

            <h3 style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 800, color: c.textPrimary }}>
              {activeQuestion.title}
            </h3>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: c.textBody, lineHeight: 1.6 }}>
              {activeQuestion.description}
            </p>

            {/* Context info */}
            {activeQuestion.context && Object.keys(activeQuestion.context).length > 0 && (
              <div style={{ background: c.fill1, border: `1px solid ${c.hairline}`, borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
                <div style={{ ...eyebrow, marginBottom: 8 }}>Background</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
                  {Object.entries(activeQuestion.context).map(([key, val]) => (
                    <div key={key}>
                      <div style={{ fontSize: 10.5, fontWeight: 700, color: c.textSecondary, textTransform: 'capitalize' }}>
                        {key.replace(/_/g, ' ')}
                      </div>
                      <div style={{ fontSize: 11.5, color: c.textBody, wordBreak: 'break-word' }}>
                        {renderContextValue(val)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Options */}
            {activeQuestion.status === 'open' && (
              <div>
                <div style={{ ...eyebrow, marginBottom: 10 }}>Choose one</div>
                {activeQuestion.options.map((option, idx) => {
                  const selected = selectedOption === option.label;
                  return (
                    <div key={idx} style={{ marginBottom: 10 }}>
                      <button
                        type="button"
                        onClick={() => handleSelectOption(option)}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          display: 'flex',
                          gap: 12,
                          alignItems: 'flex-start',
                          background: selected ? c.blue.fillSoft : c.card,
                          border: selected ? `1px solid ${c.blue.border2}` : `1px solid ${c.border}`,
                          borderRadius: 10,
                          padding: '12px 14px',
                          cursor: 'pointer',
                          fontFamily: font.ui,
                        }}
                      >
                        <span
                          aria-hidden="true"
                          style={{
                            width: 16, height: 16, borderRadius: '50%', flexShrink: 0, marginTop: 1,
                            border: selected ? `5px solid ${c.accent}` : `2px solid ${c.borderControl}`,
                            background: '#fff', boxSizing: 'border-box',
                          }}
                        />
                        <span>
                          <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700, color: c.textPrimary }}>
                            {option.label}
                          </span>
                          {option.preview_statement && (
                            <span style={{ display: 'block', fontSize: 12.5, color: c.textSecondary, marginTop: 3, lineHeight: 1.5 }}>
                              {option.preview_statement}
                            </span>
                          )}
                        </span>
                      </button>

                      {/* Sub-inputs when option is selected */}
                      {selected && option.sub_inputs && option.sub_inputs.length > 0 && (
                        <div style={{ margin: '8px 0 0 28px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                          {option.sub_inputs.map(input => (
                            <div key={input.key}>
                              <label
                                htmlFor={`ca-sub-${input.key}`}
                                style={{ display: 'block', fontSize: 12, fontWeight: 700, color: c.tableLabel, marginBottom: 4 }}
                              >
                                {input.label}
                                {input.required && <span style={{ color: c.red.text }}> *</span>}
                              </label>
                              {input.type === 'select' ? (
                                <select
                                  id={`ca-sub-${input.key}`}
                                  value={answerData[input.key] || ''}
                                  onChange={e => handleSubInputChange(input.key, e.target.value)}
                                  style={inputStyle}
                                >
                                  <option value="">Select…</option>
                                  {(input.options || []).map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                  ))}
                                </select>
                              ) : input.type === 'number' ? (
                                <input
                                  id={`ca-sub-${input.key}`}
                                  type="number"
                                  value={answerData[input.key] || ''}
                                  onChange={e => handleSubInputChange(input.key, parseFloat(e.target.value) || 0)}
                                  placeholder={input.placeholder}
                                  style={{ ...inputStyle, fontFamily: font.mono }}
                                />
                              ) : input.type === 'json' ? (
                                <textarea
                                  id={`ca-sub-${input.key}`}
                                  value={answerData[input.key] || ''}
                                  onChange={e => handleSubInputChange(input.key, e.target.value)}
                                  placeholder={input.placeholder || 'Enter JSON…'}
                                  rows={4}
                                  style={{ ...inputStyle, fontFamily: font.mono, resize: 'vertical' }}
                                />
                              ) : (
                                <input
                                  id={`ca-sub-${input.key}`}
                                  type="text"
                                  value={answerData[input.key] || ''}
                                  onChange={e => handleSubInputChange(input.key, e.target.value)}
                                  placeholder={input.placeholder}
                                  style={inputStyle}
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}

                <div style={{ marginTop: 16 }}>
                  <button
                    type="button"
                    style={btnPrimary(!selectedOption || submitting)}
                    onClick={handleSubmitAnswer}
                    disabled={!selectedOption || submitting}
                  >
                    {submitting ? 'Recording…' : 'Record this decision'}
                  </button>
                </div>
              </div>
            )}

            {activeQuestion.status !== 'open' && (
              <div
                style={{
                  background: c.green.fillSoft,
                  border: `1px solid ${c.green.border}`,
                  borderRadius: 8,
                  padding: '12px 14px',
                  fontSize: 12.5,
                  color: c.green.text,
                }}
              >
                This decision is closed.
                {activeQuestion.answered_by ? ` Answered by ${activeQuestion.answered_by}` : ''}
                {activeQuestion.answered_at ? ` on ${new Date(activeQuestion.answered_at).toLocaleString()}.` : ''}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Phase advance */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
        <button
          type="button"
          style={{ ...btnPrimary(), fontSize: 14, padding: '11px 22px' }}
          onClick={onPhaseComplete}
        >
          Run the checks →
        </button>
      </div>

      <Toast message={toast} onClose={() => setToast('')} />
    </div>
  );
};

export default QuestionQueue;
